import { DecoratorNode } from 'lexical';

// YouTube URL patterns to extract video ID (supports youtube.com, youtube-nocookie.com, youtu.be)
const YOUTUBE_REGEX = /(?:youtube(?:-nocookie)?\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=|shorts\/)|youtu\.be\/)([^"&?/\s]{11})/i;

export function extractYouTubeVideoId(url) {
  const match = url.match(YOUTUBE_REGEX);
  return match ? match[1] : null;
}

export function isYouTubeUrl(url) {
  return YOUTUBE_REGEX.test(url);
}

export class YouTubeEmbedNode extends DecoratorNode {
  __videoId;

  static getType() {
    return 'youtube-embed';
  }

  static clone(node) {
    return new YouTubeEmbedNode(node.__videoId, node.__key);
  }

  constructor(videoId, key) {
    super(key);
    this.__videoId = videoId;
  }

  createDOM() {
    const container = document.createElement('div');
    container.className = 'editor-youtube-container';

    const embedDiv = document.createElement('div');
    embedDiv.className = 'editor-youtube-embed';
    embedDiv.setAttribute('data-lexical-youtube', this.__videoId);

    const iframe = document.createElement('iframe');
    iframe.src = `https://www.youtube-nocookie.com/embed/${this.__videoId}?rel=0`;
    iframe.frameBorder = '0';
    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen';
    iframe.allowFullscreen = true;
    iframe.title = 'YouTube video';
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';

    embedDiv.appendChild(iframe);
    container.appendChild(embedDiv);

    return container;
  }

  updateDOM(prevNode, dom) {
    // Only update if videoId changed
    if (prevNode.__videoId !== this.__videoId) {
      const iframe = dom.querySelector('iframe');
      if (iframe) {
        iframe.src = `https://www.youtube-nocookie.com/embed/${this.__videoId}`;
      }
    }
    return false;
  }

  decorate() {
    return null;
  }

  exportDOM() {
    const container = document.createElement('div');
    container.className = 'video-embed youtube-embed';

    const iframe = document.createElement('iframe');
    iframe.setAttribute('src', `https://www.youtube-nocookie.com/embed/${this.__videoId}?rel=0`);
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen');
    iframe.setAttribute('allowfullscreen', 'true');
    iframe.setAttribute('title', 'YouTube video');
    iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');

    container.appendChild(iframe);

    return { element: container };
  }

  static importDOM() {
    return {
      div: (node) => {
        if (!node.classList.contains('youtube-embed')) {
          return null;
        }
        return {
          conversion: (domNode) => {
            const iframe = domNode.querySelector('iframe');
            if (iframe) {
              const src = iframe.getAttribute('src') || '';
              const videoId = extractYouTubeVideoId(src);
              if (videoId) {
                return { node: new YouTubeEmbedNode(videoId) };
              }
            }
            return null;
          },
          priority: 1,
        };
      },
    };
  }

  exportJSON() {
    return {
      type: 'youtube-embed',
      version: 1,
      videoId: this.__videoId,
    };
  }

  static importJSON(json) {
    return new YouTubeEmbedNode(json.videoId);
  }

  getVideoId() {
    return this.__videoId;
  }

  isInline() {
    return false;
  }

  isIsolated() {
    return true;
  }

  isKeyboardSelectable() {
    return true;
  }
}

export function $createYouTubeEmbedNode(videoId) {
  return new YouTubeEmbedNode(videoId);
}

export function $isYouTubeEmbedNode(node) {
  return node instanceof YouTubeEmbedNode;
}
